<script>
  import { onMount, onDestroy } from 'svelte';
  import { fetchEgresoDrops, saveEgresoDrop, removeEgresoDrop, subscribeToEgresoDrops, unsubscribeFromEgresoDrops } from '../../controllers/egresoDropController.js';
  import { fetchEgresoDropEscoltas, saveDropEscoltas, subscribeToEgresoDropEscoltas, unsubscribeFromEgresoDropEscoltas } from '../../controllers/egresoDropEscoltaController.js';
  import Swal from 'sweetalert2';
  import DropMap from './DropMap.svelte';
  
  export let selectedItem;
  export let deliveries;
  export let transportes;
  export let vehiculos;
  export let escoltas;
  export let dispatch;
  export let closePane;

  let egresoDrops = [];
  let editMode = false;
  let editingDrop = null;
  let isAddingNew = false;
  
  // Form fields for new/edit drop
  let deliveryMethod = '';
  let selectedDelivery = '';
  let selectedVehiculo = '';
  let selectedTransporte = '';
  let targetRef = '';
  let transporteRef = '';
  let selectedEscoltas = [];

  // Load existing drops
  async function loadEgresoDrops() {
    if (selectedItem?.id) {
      try {
        egresoDrops = await fetchEgresoDrops(selectedItem.id);
        console.log("Loaded egreso drops:", egresoDrops);
        
        // Load escoltas for each drop
        for (let drop of egresoDrops) {
          drop.escoltas = await fetchEgresoDropEscoltas(drop.id);
        }
      } catch (error) {
        console.error("Error loading egreso drops:", error);
        egresoDrops = [];
      }
    }
  }

  // Reset form fields
  function resetForm() {
    deliveryMethod = '';
    selectedDelivery = '';
    selectedVehiculo = '';
    selectedTransporte = '';
    targetRef = '';
    transporteRef = '';
    selectedEscoltas = [];
    editingDrop = null;
    isAddingNew = false;
  }

  // Add new drop
  function addNewDrop() {
    resetForm();
    isAddingNew = true;
    editMode = true;
  }

  // Edit existing drop
  async function editDrop(drop) {
    editingDrop = drop;
    deliveryMethod = drop.delivery_method || '';
    selectedDelivery = drop.delivery || '';
    selectedVehiculo = drop.vehiculo || '';
    selectedTransporte = drop.transporte || '';
    targetRef = drop.target_ref || '';
    transporteRef = drop.transporte_ref || '';
    
    // Load existing escoltas for this specific drop
    try {
      const dropEscoltas = await fetchEgresoDropEscoltas(drop.id);
      selectedEscoltas = dropEscoltas.map(e => e.escolta || e.expand?.escolta).filter(Boolean);
    } catch (error) {
      console.error("Error loading drop escoltas:", error);
      selectedEscoltas = [];
    }
    
    isAddingNew = false;
    editMode = true;
  }

  // Delete drop
  async function deleteDrop(dropId) {
    const result = await Swal.fire({
      title: '¿Está seguro?',
      text: 'Esta acción eliminará el drop de entrega. ¡No se puede deshacer!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar'
    });
    if (result.isConfirmed) {
      try {
        await removeEgresoDrop(dropId);
        await loadEgresoDrops(); // Reload the list
      } catch (error) {
        console.error("Error deleting drop:", error);
      }
    }
  }

  // Save drop
  async function saveDrop() {
    if (!deliveryMethod) {
      alert('Por favor seleccione un método de entrega');
      return;
    }

    const dropData = {
      egresoCab: selectedItem.id,
      delivery_method: deliveryMethod,
      target_ref: targetRef
    };

    // Add conditional fields based on delivery method
    if (deliveryMethod === 'Repartidor') {
      if (!selectedDelivery || !selectedVehiculo) {
        alert('Por favor complete todos los campos requeridos para Repartidor');
        return;
      }
      dropData.delivery = selectedDelivery;
      dropData.vehiculo = selectedVehiculo;
    } else if (deliveryMethod === 'Transporte') {
      if (!selectedDelivery || !selectedVehiculo || !selectedTransporte || !transporteRef) {
        alert('Por favor complete todos los campos requeridos para Transporte');
        return;
      }
      dropData.delivery = selectedDelivery;
      dropData.vehiculo = selectedVehiculo;
      dropData.transporte = selectedTransporte;
      dropData.transporte_ref = transporteRef;
    }

    try {
      let savedDrop;
      if (isAddingNew) {
        dropData.sta = 'READY'; // Ensure default status
        savedDrop = await saveEgresoDrop(dropData);
      } else {
        savedDrop = await saveEgresoDrop(dropData, editingDrop.id);
      }
      
      // Save escoltas for this specific drop
      const dropId = isAddingNew ? savedDrop.id : editingDrop.id;
      console.log("About to save escoltas for dropId:", dropId);
      console.log("Selected escoltas:", selectedEscoltas);
      await saveDropEscoltas(dropId, selectedEscoltas);
      console.log("Escoltas saved successfully");
      
      await loadEgresoDrops(); // Reload the list
      resetForm();
      editMode = false;
    } catch (error) {
      console.error("Error saving drop:", error);
    }
  }

  // Cancel edit
  function cancelEdit() {
    resetForm();
    editMode = false;
  }

  // Get display name for delivery
  function getDeliveryName(deliveryId) {
    const delivery = deliveries.find(d => d.id === deliveryId);
    return delivery ? delivery.nombre : 'No encontrado';
  }

  // Get display name for vehicle
  function getVehicleName(vehicleId) {
    const vehicle = vehiculos.find(v => v.id === vehicleId);
    return vehicle ? `${vehicle.placa} / ${vehicle.marca} ${vehicle.modelo} (${vehicle.color})` : 'No encontrado';
  }

  // Get display name for transporte
  function getTransporteName(transporteId) {
    const transporte = transportes.find(t => t.id === transporteId);
    return transporte ? transporte.descrip : 'No encontrado';
  }

  // Toggle escolta selection
  function toggleEscolta(escoltaId) {
    const index = selectedEscoltas.indexOf(escoltaId);
    if (index === -1) {
      selectedEscoltas = [...selectedEscoltas, escoltaId];
    } else {
      selectedEscoltas = selectedEscoltas.filter(id => id !== escoltaId);
    }
  }

  // Check if escolta is selected
  function isEscoltaSelected(escoltaId) {
    return selectedEscoltas.includes(escoltaId);
  }

  // Get escolta names for display
  function getEscoltaNames(escoltaItems) {
    if (!escoltaItems || escoltaItems.length === 0) return 'Ninguno';
    
    const names = escoltaItems
      .map(item => {
        const escolta = item.expand?.escolta || item.escolta;
        return escolta ? escolta.nombre : null;
      })
      .filter(Boolean)
      .join(', ');
      
    return names || 'Ninguno';
  }

  // Initialize component
  onMount(() => {
    loadEgresoDrops();
    subscribeToEgresoDrops(loadEgresoDrops);
    subscribeToEgresoDropEscoltas(loadEgresoDrops);
  });

  onDestroy(() => {
    unsubscribeFromEgresoDrops();
    unsubscribeFromEgresoDropEscoltas();
  });
</script>

<div class="space-y-4">
  <div>
    <h2 class="text-lg font-bold text-blueGray-700 mb-4">Definir Drops de Entrega</h2>

    <!-- Add New Drop Button -->
    <div class="mb-4">
      <button
        class="bg-emerald-500 text-white active:bg-emerald-600 font-bold uppercase text-sm px-4 py-2 rounded shadow hover:shadow-lg outline-none focus:outline-none ease-linear transition-all duration-150"
        on:click={addNewDrop}
        disabled={editMode}
      >
        + Agregar Nuevo Drop
      </button>
    </div>

    <!-- Edit Form -->
    {#if editMode}
      <div class="mb-6 p-4 border rounded bg-blueGray-50">
        <h3 class="font-medium text-blueGray-700 mb-4">
          {isAddingNew ? 'Agregar Nuevo Drop' : 'Editar Drop'}
        </h3>
        
        <form on:submit|preventDefault={saveDrop}>
          <div class="mb-4">
            <label class="block text-blueGray-700 text-sm font-bold mb-2" for="deliveryMethod">
              Método de Entrega *
            </label>
            <select
              id="deliveryMethod"
              class="shadow appearance-none border rounded w-full py-2 px-3 text-blueGray-700 leading-tight focus:outline-none focus:shadow-outline"
              bind:value={deliveryMethod}
              required
            >
              <option value="">Seleccione un método</option>
              <option value="Despacho">Despacho</option>
              <option value="Repartidor">Repartidor</option>
              <option value="Transporte">Transporte</option>
            </select>
          </div>

          <!-- Conditional Fields -->
          {#if deliveryMethod === 'Repartidor'}
            <div class="mb-4">
              <label class="block text-blueGray-700 text-sm font-bold mb-2" for="selectedDelivery">
                Datos del Repartidor
              </label>
              <select
                id="selectedDelivery"
                class="shadow appearance-none border rounded w-full py-2 px-3 text-blueGray-700 leading-tight focus:outline-none focus:shadow-outline"
                bind:value={selectedDelivery}
                required
              >
                <option value="">Seleccione repartidor</option>
                {#each deliveries as delivery}
                  <option value={delivery.id}>{delivery.nombre}</option>
                {/each}
              </select>
            </div>
            
            <div class="mb-4">
              <label class="block text-blueGray-700 text-sm font-bold mb-2" for="selectedVehiculo">
                Vehículo
              </label>
              <select
                id="selectedVehiculo"
                class="shadow appearance-none border rounded w-full py-2 px-3 text-blueGray-700 leading-tight focus:outline-none focus:shadow-outline"
                bind:value={selectedVehiculo}
                required
              >
                <option value="">Seleccione vehículo</option>
                {#each vehiculos as vehiculo}
                  <option value={vehiculo.id}>{vehiculo.placa} / {vehiculo.marca} {vehiculo.modelo} - {vehiculo.color}</option>
                {/each}
              </select>
            </div>
          {:else if deliveryMethod === 'Transporte'}
            <div class="mb-4">
              <label class="block text-blueGray-700 text-sm font-bold mb-2" for="selectedDelivery">
                Datos del Repartidor
              </label>
              <select
                id="selectedDelivery"
                class="shadow appearance-none border rounded w-full py-2 px-3 text-blueGray-700 leading-tight focus:outline-none focus:shadow-outline"
                bind:value={selectedDelivery}
                required
              >
                <option value="">Seleccione repartidor</option>
                {#each deliveries as delivery}
                  <option value={delivery.id}>{delivery.nombre}</option>
                {/each}
              </select>
            </div>
            
            <div class="mb-4">
              <label class="block text-blueGray-700 text-sm font-bold mb-2" for="selectedVehiculo">
                Vehículo
              </label>
              <select
                id="selectedVehiculo"
                class="shadow appearance-none border rounded w-full py-2 px-3 text-blueGray-700 leading-tight focus:outline-none focus:shadow-outline"
                bind:value={selectedVehiculo}
                required
              >
                <option value="">Seleccione vehículo</option>
                {#each vehiculos as vehiculo}
                  <option value={vehiculo.id}>{vehiculo.placa} / {vehiculo.marca} {vehiculo.modelo} - {vehiculo.color}</option>
                {/each}
              </select>
            </div>
            
            <div class="mb-4">
              <label class="block text-blueGray-700 text-sm font-bold mb-2" for="selectedTransporte">
                Datos del Transporte
              </label>
              <select
                id="selectedTransporte"
                class="shadow appearance-none border rounded w-full py-2 px-3 text-blueGray-700 leading-tight focus:outline-none focus:shadow-outline"
                bind:value={selectedTransporte}
                required
              >
                <option value="">Seleccione transporte</option>
                {#each transportes as transporte}
                  <option value={transporte.id}>{transporte.descrip}</option>
                {/each}
              </select>
            </div>
            
            <div class="mb-4">
              <label class="block text-blueGray-700 text-sm font-bold mb-2" for="transporteRef">
                Referencia de Transporte
              </label>
              <input
                id="transporteRef"
                type="text"
                class="shadow appearance-none border rounded w-full py-2 px-3 text-blueGray-700 leading-tight focus:outline-none focus:shadow-outline"
                bind:value={transporteRef}
                placeholder="Ingrese la referencia de transporte"
                required
              />
            </div>
          {/if}

          <!-- Escoltas Selection for Repartidor and Transporte -->
          {#if deliveryMethod === 'Repartidor' || deliveryMethod === 'Transporte'}
            <div class="mb-4">
              <label class="block text-blueGray-700 text-sm font-bold mb-2" for="">
                Escoltas
              </label>
              <div class="bg-white shadow overflow-hidden border rounded-md">
                {#if escoltas && escoltas.length > 0}
                  <ul class="divide-y divide-gray-200">
                    {#each escoltas as escolta}
                      <li class="relative">
                        <label class="flex items-center px-4 py-3 hover:bg-blueGray-50 cursor-pointer">
                          <input 
                            type="checkbox" 
                            class="mr-3 h-4 w-4 text-blue-600"
                            checked={isEscoltaSelected(escolta.id)}
                            on:change={() => toggleEscolta(escolta.id)} 
                          />
                          <span class="block text-sm text-blueGray-700">{escolta.nombre}</span>
                        </label>
                      </li>
                    {/each}
                  </ul>
                {:else}
                  <div class="p-4 text-sm text-blueGray-500 text-center">
                    No hay escoltas disponibles
                  </div>
                {/if}
              </div>
              <div class="mt-2 text-xs text-blueGray-500">
                {selectedEscoltas.length} escolta(s) seleccionado(s)
              </div>
            </div>
          {/if}

          <!-- Referencia de Entrega for Repartidor and Transporte -->
          {#if deliveryMethod === 'Repartidor' || deliveryMethod === 'Transporte'}
            <div class="mb-4">
              <label class="block text-blueGray-700 text-sm font-bold mb-2" for="targetRef">
                Referencia de Entrega
              </label>
              <input
                id="targetRef"
                type="text"
                class="shadow appearance-none border rounded w-full py-2 px-3 text-blueGray-700 leading-tight focus:outline-none focus:shadow-outline"
                bind:value={targetRef}
                placeholder="Ingrese la referencia de entrega"
              />
            </div>
          {/if}

          <!-- Maps for editing geo fields -->
          {#if deliveryMethod === 'Repartidor' || deliveryMethod === 'Transporte'}
            <DropMap drop={editingDrop || { id: 'new', geo_target_ref: '' }} geoField="geo_target_ref" />
          {/if}

          <!-- Save and Cancel Buttons -->
          <div class="flex items-center justify-end p-6 border-t border-solid border-blueGray-200 rounded-b">
            <button
              class="bg-blueGray-600 text-white active:bg-blueGray-700 font-bold uppercase text-sm px-6 py-3 rounded shadow hover:shadow-lg outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150"
              type="button"
              on:click={cancelEdit}
            >
              Cancelar
            </button>
            <button
              class="bg-emerald-500 text-white active:bg-emerald-600 font-bold uppercase text-sm px-6 py-3 rounded shadow hover:shadow-lg outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150"
              type="submit"
            >
              {isAddingNew ? 'Crear Drop' : 'Guardar Cambios'}
            </button>
          </div>
        </form>
      </div>
    {/if}

    <!-- Drops List -->
    <div class="space-y-4">
      <h3 class="font-medium text-blueGray-700">Drops Configurados ({egresoDrops.length})</h3>
      
      {#if egresoDrops.length > 0}
        {#each egresoDrops as drop}
          <div class="p-4 border rounded bg-white shadow-sm">
            <div class="flex justify-between items-start mb-3">
              <div class="flex-1">
                <div class="flex items-center gap-2 mb-2">
                  <span class="inline-block px-2 py-1 text-xs font-semibold rounded-full 
                    {drop.delivery_method === 'Despacho' ? 'bg-blue-100 text-blue-800' : 
                     drop.delivery_method === 'Repartidor' ? 'bg-green-100 text-green-800' : 
                     'bg-purple-100 text-purple-800'}">
                    {drop.delivery_method}
                  </span>
                  {#if drop.target_ref}
                    <span class="text-sm text-gray-600">Ref: {drop.target_ref}</span>
                  {/if}
                </div>
                
                <!-- Delivery Method Specific Info -->
                {#if drop.delivery_method === 'Repartidor' && drop.expand?.delivery}
                  <p class="text-sm text-gray-700"><strong>Repartidor:</strong> {drop.expand.delivery.nombre}</p>
                  {#if drop.expand?.vehiculo}
                    <p class="text-sm text-gray-700"><strong>Vehículo:</strong> {drop.expand.vehiculo.placa} / {drop.expand.vehiculo.marca} {drop.expand.vehiculo.modelo}</p>
                  {/if}
                {:else if drop.delivery_method === 'Transporte' && drop.expand?.transporte}
                  <p class="text-sm text-gray-700"><strong>Transporte:</strong> {drop.expand.transporte.descrip}</p>
                  {#if drop.transporte_ref}
                    <p class="text-sm text-gray-700"><strong>Ref. Transporte:</strong> {drop.transporte_ref}</p>
                  {/if}
                {/if}
                
                <!-- Additional Info -->
                {#if drop.target_ref}
                  <p class="text-sm text-gray-700"><strong>Ref. Entrega:</strong> {drop.target_ref}</p>
                {/if}
                
                <!-- Escoltas Information for this specific drop -->
                {#if drop.escoltas && drop.escoltas.length > 0}
                  <p class="text-sm text-gray-700">
                    <strong>Escoltas:</strong> {getEscoltaNames(drop.escoltas)}
                  </p>
                {/if}
              </div>
              
              <div class="flex gap-2">
                <button
                  class="bg-blueGray-600 text-white active:bg-blueGray-700 font-bold uppercase text-xs px-3 py-1 rounded shadow hover:shadow-lg outline-none focus:outline-none ease-linear transition-all duration-150"
                  on:click={() => editDrop(drop)}
                  disabled={editMode}
                >
                  Editar
                </button>
                <button
                  class="bg-red-500 text-white active:bg-red-600 font-bold uppercase text-xs px-3 py-1 rounded shadow hover:shadow-lg outline-none focus:outline-none ease-linear transition-all duration-150"
                  on:click={() => deleteDrop(drop.id)}
                  disabled={editMode}
                >
                  Eliminar
                </button>
              </div>
            </div>
            <!-- Maps for each drop -->
            {#if drop.delivery_method === 'Repartidor' || drop.delivery_method === 'Transporte'}
              <DropMap {drop} geoField="geo_target_ref" />
            {/if}
          </div>
        {/each}
      {:else}
        <div class="p-4 border rounded bg-gray-50 text-center text-gray-500">
          No hay drops configurados para este egreso.
        </div>
      {/if}
    </div>
  </div>
</div>